import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for managing page meta tags, Open Graph, and structured data
 * Usage: <SEO title="Page Title" description="..." />
 */
const SEO = ({
  title,
  fullTitle,
  description,
  keywords,
  image,
  url,
  type = 'website',
  structuredData,
  noindex = false,
}) => {
  const siteUrl = 'https://www.hillpointglobal.com';
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  // Default card is generated at 1200x630 by scripts/generate-og-image.js
  const defaultImage = `${siteUrl}/og-image.png`;
  const usesDefaultImage = !image;
  const ogImage = image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : defaultImage;
  
  // Default meta data
  const defaultTitle = 'HillPoint Global - Premium Acoustic Solutions';
  const defaultDescription = 'Leading provider of acoustic engineering solutions in the Middle East and India. Specializing in acoustic panels, soundproofing, and architectural acoustics for corporate, education, and hospitality sectors.';
  const defaultKeywords = 'acoustic solutions, soundproofing, acoustic panels, architectural acoustics, noise reduction, sound absorption, acoustic treatment, HillPoint Global, Middle East acoustics, India acoustics';
  
  const pageTitle = fullTitle || (title ? `${title} | HillPoint Global` : defaultTitle);
  const metaDescription = description || defaultDescription;
  const metaKeywords = keywords || defaultKeywords;

  const jsonLdBlocks = structuredData
    ? (Array.isArray(structuredData) ? structuredData : [structuredData]).filter(Boolean)
    : [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={ogImage} />
      {/* Dimensions are only known for the generated default card; page-supplied images vary */}
      {usesDefaultImage && <meta property="og:image:width" content="1200" />}
      {usesDefaultImage && <meta property="og:image:height" content="630" />}
      <meta property="og:site_name" content="HillPoint Global" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional Meta Tags */}
      <meta name="author" content="HillPoint Global" />
      
      {/* Geo Tags */}
      <meta name="geo.region" content="QA" />
      <meta name="geo.placename" content="Doha, Qatar" />
      
      {/* Structured Data (JSON-LD) — one script tag per object (arrays are not valid as a single JSON-LD root) */}
      {jsonLdBlocks.map((block, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;

